import { Component, Watch, Event, EventEmitter, Element, Listen, State, Prop, h } from '@stencil/core';
import { CalendarEntry } from '../../../utils/calendar-entry';
import { Calendar } from '../../../utils/calendar';

@Component({
  tag: 'lab-ds-date-picker',
  styleUrl: 'lab-ds-date-picker.css',
  shadow: true,
})
export class LabDsDatePicker {
  @State() isCalendarVisible: boolean = false;

  @State() isYearVisible: boolean = false;

  @State() isMonthVisible: boolean = false;

  @Prop() value!: string;

  @Prop() titleInput: string;

  @Prop() disabled: boolean = false;

  @Prop() helperText: string;

  @Prop() state: 'error' | 'default' = 'default';

  @Prop() size: 'small' | 'medium' = 'small';

  @Event() changeDatePicker: EventEmitter<string>;

  @Event() handleConfirmDatePicker: EventEmitter;

  @Prop() dayNames = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

  @Prop() monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  @State() monthChecked: string;

  @State() yearChecked: number;

  @Prop() showFillDays = true;

  @State() date = Calendar.getToday();

  @State() daysInMonth: number[];

  @State() selectedDate: CalendarEntry;

  @State() eventDates = [];

  @Event({ eventName: 'dayChanged', composed: true, cancelable: true, bubbles: true }) dayChanged: EventEmitter<string>;

  @Event({ eventName: 'monthChanged', composed: true, cancelable: true, bubbles: true }) monthChanged: EventEmitter<CalendarEntry>;

  @Event({ eventName: 'yearChanged', composed: true, cancelable: true, bubbles: true }) yearChanged: EventEmitter<CalendarEntry>;

  private fillStartCount: number;
  private fillEndCount: number;
  readonly today: CalendarEntry;

  constructor() {
    this.today = Calendar.getToday();
  }

  @Watch('date')
  watchDate(date: CalendarEntry): void {
    if ('month' in date && 'year' in date) {
      this.selectedDate = date;
    }
  }

  componentWillLoad() {
    this.setCalendarDetails();
  }

  setCalendarDetails(): void {
    const date = this.getValidDate();
    const calendar = new Calendar(date.year, date.month);
    this.daysInMonth = calendar.getCalendarDays();

    this.fillStartCount = calendar.getFillStartCount();
    this.fillEndCount = calendar.daysInCalendar - calendar.getFillEndCount();
  }

  getValidDate(): CalendarEntry {
    let date = this.date;
    if (!('month' in this.date && 'year' in this.date)) {
      date = this.today;
    }

    return date;
  }

  dayChangedHandler(calendarEntry: CalendarEntry): void {
    this.changeDatePicker.emit(`${calendarEntry.year}-${String(calendarEntry.month).padStart(2, '0')}-${String(calendarEntry.day).padStart(2, '0')}`);
    this.isCalendarVisible = false;
  }

  daySelectedHandler = (day): void => {
    this.selectedDate = {
      day,
      month: this.date.month,
      year: this.date.year,
    };

    this.dayChangedHandler(this.selectedDate);
  };

  monthChangedHandler(calendarEntry: CalendarEntry): void {
    this.monthChanged.emit(calendarEntry);
  }

  switchToPreviousMonth = (): void => {
    if (this.date.month !== 1) {
      this.date.month -= 1;
    } else {
      this.date.month = 12;
      this.date.year -= 1;
    }

    if (typeof this.date !== 'undefined') {
      delete this.date.day;
    }

    this.setCalendarDetails();
    this.monthChangedHandler(this.date);
  };

  switchToNextMonth = (): void => {
    if (this.date.month !== 12) {
      this.date.month += 1;
    } else {
      this.date.month = 1;
      this.date.year += 1;
    }

    delete this.date.day;

    this.setCalendarDetails();
    this.monthChangedHandler(this.date);
  };

  yearChangedHandler(calendarEntry: CalendarEntry): void {
    this.yearChanged.emit(calendarEntry);
  }

  switchToPreviousYear = (): void => {
    this.date.year -= 1;

    if (typeof this.date !== 'undefined') {
      delete this.date.day;
    }

    this.setCalendarDetails();
    this.yearChangedHandler(this.date);
  };

  switchToNextYear = (): void => {
    this.date.year += 1;

    delete this.date.day;

    this.setCalendarDetails();
    this.monthChangedHandler(this.date);
  };

  getDigitClassNames = (day: number, month: number, year: number, index: number): string => {
    let classNameDigit = [];
    if (day.toString().length === 1) {
      classNameDigit.push('padding-single-digit');
    }

    if (this.isToday(day, month, year, index)) {
      classNameDigit.push('active');
    }

    if (this.isSelectedDay(day, index)) {
      classNameDigit.push('selected');
    }

    if (this.eventDates.includes(day)) {
      classNameDigit.push('has-event');
    }

    return classNameDigit.join(' ');
  };

  isToday(day: number, month: number, year: number, index: number): boolean {
    return (
      this.today.day === day && this.today.month === month && this.today.year === year && this.today.year === year && !(index < this.fillStartCount || index >= this.fillEndCount)
    );
  }

  isSelectedDay(day: number, index: number) {
    return (
      typeof this.selectedDate !== 'undefined' &&
      this.selectedDate.day === day &&
      this.selectedDate.month === this.date.month &&
      this.selectedDate.year === this.date.year &&
      !(index < this.fillStartCount || index >= this.fillEndCount)
    );
  }

  handleSelect() {
    if (this.disabled) return;

    this.isCalendarVisible = !this.isCalendarVisible;
  }

  handleConfirm() {
    this.isCalendarVisible = false;
    this.handleConfirmDatePicker.emit();
  }

  handleCancel() {
    this.isCalendarVisible = false;
    this.selectedDate.day = null;
    this.selectedDate.month = null;
    this.selectedDate.year = null;
    this.changeDatePicker.emit('');
  }

  // eslint-disable-next-line @stencil-community/element-type
  @Element() el: HTMLElement;

  @Listen('click', { target: 'window' })
  checkForClickOutside(ev) {
    if (this.el.contains(ev.target)) {
      return;
    }
    this.isCalendarVisible = false;
  }

  render() {
    const isMonthVisibleIcon = this.isMonthVisible ? 'expand_less' : 'expand_more';

    const isYearVisibleIcon = this.isYearVisible ? 'expand_less' : 'expand_more';

    const iconIsHidden = this.isMonthVisible || this.isYearVisible ? 'icon-hidden' : '';

    const date = this.getValidDate();

    const dateActual = new Date();

    let yearsName = [];

    for (let i = dateActual.getFullYear() + 3; i >= 1900; i--) {
      yearsName.push(i);
    }

    return (
      <div class={`v4-input ${this.state} ${this.size} ${this.disabled && 'disabled'}`}>
        <div class="input-title-container">
          <span class="title-text">{this.titleInput}</span>
          <div
            onClick={() => {
              this.handleSelect();
            }}
            class={`input-icon`}
          >
            <input disabled={this.disabled} class="input-field" type="date" value={this.value} readonly />

            <lab-ds-icon-not-selectable class={`icon right`} size="small" icon="calendar_month" />
          </div>
        </div>

        {this.isCalendarVisible && (
          <div class="calendar-container">
            <div class="hints">
              <div class="calendar-visible-container">
                <div class="calendar material">
                  <div class="header">
                    <div class="calendar-select calendar-month-select">
                      <lab-ds-icon-selectable class={`${iconIsHidden}`} onClick={this.switchToPreviousMonth} size="s-medium" icon="chevron_left" />
                      <span
                        onClick={() => {
                          this.isMonthVisible = !this.isMonthVisible;
                          this.isYearVisible = false;
                        }}
                        class="calendar-select-text"
                      >
                        {this.monthNames[date.month - 1]?.slice(0, 3)} <lab-ds-icon-selectable size="small" icon={isMonthVisibleIcon} />
                      </span>
                      <lab-ds-icon-selectable class={`${iconIsHidden}`} onClick={this.switchToNextMonth} size="s-medium" icon="chevron_right" />
                    </div>
                    <div class="calendar-select calendar-year-select">
                      <lab-ds-icon-selectable class={`${iconIsHidden}`} onClick={this.switchToPreviousYear} size="s-medium" icon="chevron_left" />
                      <span
                        onClick={() => {
                          this.isYearVisible = !this.isYearVisible;
                          this.isMonthVisible = false;
                        }}
                        class="calendar-select-text"
                      >
                        {date.year}
                        <lab-ds-icon-selectable size="small" icon={isYearVisibleIcon} />
                      </span>
                      <lab-ds-icon-selectable class={`${iconIsHidden}`} onClick={this.switchToNextYear} size="s-medium" icon="chevron_right" />
                    </div>
                  </div>
                  <div class="calendar-select-relative">
                    {this.isMonthVisible && (
                      <div class="calendar-month-select-container scroll-customized">
                        {this.monthNames.map((monthName, index) => (
                          <div class="calendar-select-radio-button" key={index}>
                            <lab-ds-radio-button
                              label={monthName}
                              checked={this.monthChecked === monthName}
                              onHandleRadioButton={() => {
                                this.date.month = index + 1;
                                this.monthChecked = monthName;
                                this.monthChangedHandler(this.date);
                                this.isMonthVisible = false;
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {this.isYearVisible && (
                      <div class="calendar-year-select-container scroll-customized">
                        {yearsName?.map((yearsName, index) => (
                          <div class="calendar-select-radio-button" key={index}>
                            <lab-ds-radio-button
                              label={yearsName}
                              checked={this.yearChecked === yearsName}
                              onHandleRadioButton={() => {
                                this.date.year = yearsName;
                                this.yearChecked = yearsName;
                                this.monthChangedHandler(this.date);
                                this.isYearVisible = false;
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div class="day-names">
                    {this.dayNames.map(dayName => (
                      <span>{dayName}</span>
                    ))}
                  </div>
                  <div class="days-in-month">
                    {this.daysInMonth.map((day, index) => {
                      const classNameDigit = this.getDigitClassNames(day, date.month, date.year, index);
                      if (index < this.fillStartCount || index >= this.fillEndCount) {
                        return (
                          <span class="disabled">
                            <span>{this.showFillDays ? day : ''}</span>
                          </span>
                        );
                      } else {
                        return (
                          <span tabIndex={index} onClick={() => this.daySelectedHandler(day)}>
                            <i class={classNameDigit}>{day}</i>
                          </span>
                        );
                      }
                    })}
                  </div>
                  <div class="calendar-actions">
                    <lab-ds-button size="small" onClick={() => this.handleCancel()} leadingIcon="cancel" label="Cancelar" variant="danger-outlined" />
                    <lab-ds-button size="small" onClick={() => this.handleConfirm()} label="Confirmar" variant="primary" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {this.helperText && <span class="helper-text">{this.helperText}</span>}
      </div>
    );
  }
}
